import { MapDrawing } from '@watersport/ui-react-icons'
import { Button, Slider } from '@watersport/ui-react-next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { cn } from 'features/common/helpers/cn'

export interface DistanceSelectorProps {
  show: boolean
  distance: number | undefined
  onChangeDistance: (distance: number | undefined) => void
  onChangeViewMode: (mode: 'select' | 'drawing') => void
  onSearch: () => void
}

const DistanceSelector: React.FC<DistanceSelectorProps> = ({
  show,
  distance: pDistance,
  onChangeDistance,
  onSearch,
  onChangeViewMode,
}) => {
  const [value, setValue] = useState<number>(1)
  const [distance, setDistance] = useState<number | undefined>(undefined)

  useEffect(() => {
    setDistance(pDistance)
  }, [pDistance])

  useEffect(() => {
    if (!distance) {
      setValue(27)
    } else if (distance < 16) {
      setValue(distance)
    } else if (distance < 51) {
      setValue(Math.floor((distance - 15) / 5 + 15))
    } else if (distance < 101) {
      setValue(Math.floor((distance - 100) / 10 + 27))
    } else {
      setValue(27)
    }
  }, [distance])

  const updateDistance = useCallback((v: number) => {
    if (v < 16) {
      return v
    }
    if (v < 23) {
      return 15 + (v - 15) * 5
    }
    if (v < 28) {
      return 50 + (v - 22) * 10
    }
    return 27
  }, [])

  const searchButtonText = useMemo(
    () => (distance ? `Search here (+${distance}km)` : 'Search here'),
    [distance],
  )

  const onChanged = useCallback(
    (v: number) => {
      const va = updateDistance(v)
      onChangeDistance(va)
    },
    [onChangeDistance, updateDistance],
  )

  const onChange = (v: number) => {
    setValue(v)
    const va = updateDistance(v)
    setDistance(va)
  }

  return (
    <div
      className={cn(
        'absolute bottom-0 left-0 right-0 flex flex-col gap-4  transition-transform duration-300',
        show ? 'translate-y-0' : 'translate-y-full',
      )}
      data-testid="distance-selector-container"
    >
      <div
        className="h-[46px] flex items-center justify-center gap-2 px-5 ml-5 mr-[86px] bg-white border border-blue-400 rounded-lg"
        data-testid="distance-slider-container"
      >
        <span
          className="flex justify-center items-center text-base font-normal w-10"
          data-testid="distance-value"
        >
          {distance ? `${distance}km` : '∞'}
        </span>
        <Slider
          min={1}
          max={27}
          step={1}
          value={value}
          onChanged={onChanged}
          onChange={onChange}
          dataTestId="distance-slider"
        />
      </div>

      <div className="bg-blue-200 rounded-t-3xl px-5 py-4 flex gap-4" data-testid="action-buttons">
        <Button
          containerClassName="w-auto"
          className={cn('pr-5')}
          variant={'outline'}
          text={'Draw'}
          leftIcon={<MapDrawing className="w-[20px] h-[18px] m-[2px]" />}
          rounded
          size={'xl'}
          onClick={() => onChangeViewMode('drawing')}
          data-testid="draw-button"
        />
        <Button
          className={cn('')}
          variant={'default'}
          text={searchButtonText}
          rounded
          size={'xl'}
          onClick={() => onSearch()}
          data-testid="search-button"
        />
      </div>
    </div>
  )
}

export default DistanceSelector
